/* Hover readout shared by every chart.
 *
 * The charts are hand-drawn SVG with no library, so there is no built-in
 * hit-testing. Rather than attach listeners to individual bars — which
 * leaves dead gaps between them, and misses thin lines entirely — each
 * chart captures the pointer over its whole plot area and converts the
 * cursor's x into an index. Every pixel of the chart is therefore live,
 * including the gaps, which is what makes a dense 19-bar month usable.
 *
 * Charts supply the tooltip's contents; this file owns hit-testing and
 * placement only.
 */

import { useCallback, useRef, useState } from 'react'
import type { ReactNode, RefObject } from 'react'

export interface HoverPoint { i: number; x: number; y: number }

export interface Hover {
  ref: RefObject<HTMLDivElement>
  point: HoverPoint | null
  bind: {
    onMouseMove: (e: React.MouseEvent) => void
    onMouseLeave: () => void
  }
}

/**
 * Hit-testing for a chart whose data sits in `count` equal-width bands —
 * every bar chart here, and the line charts, which share the same slots.
 */
export function useBandHover(count: number): Hover {
  const ref = useRef<HTMLDivElement>(null)
  const [point, setPoint] = useState<HoverPoint | null>(null)

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    const el = ref.current
    if (!el || count < 1) return
    const r = el.getBoundingClientRect()
    if (r.width <= 0) return
    const x = e.clientX - r.left
    const i = Math.min(count - 1, Math.max(0, Math.floor((x / r.width) * count)))
    setPoint({ i, x, y: e.clientY - r.top })
  }, [count])

  const onMouseLeave = useCallback(() => setPoint(null), [])

  return { ref, point, bind: { onMouseMove, onMouseLeave } }
}

/**
 * The readout itself. Positioned against the plot box rather than the
 * cursor's own coordinates so it cannot drift outside the panel, and
 * flipped above the pointer near the bottom edge.
 *
 * `pointer-events: none` throughout — a tooltip that can be hovered would
 * fight the chart underneath it for the pointer and flicker.
 */
export function Tooltip({ x, y, width, children }: {
  x: number
  y: number
  /** Plot width, so the card can be kept inside it. */
  width: number
  children: ReactNode
}) {
  const CARD = 180
  const half = CARD / 2
  // Clamp rather than let it overhang: at the first and last band the card
  // would otherwise sit half outside the panel.
  const left = Math.min(Math.max(x, half + 4), Math.max(half + 4, width - half - 4))

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute',
        left,
        top: y,
        transform: `translate(-50%, ${y > 90 ? 'calc(-100% - 14px)' : '14px'})`,
        width: CARD,
        pointerEvents: 'none',
        zIndex: 5,
        background: 'var(--color-bg)',
        border: '1px solid var(--color-divider)',
        boxShadow: 'var(--shadow-md)',
        padding: '8px 10px',
        fontSize: 12,
        lineHeight: 1.45,
        color: 'var(--color-text)',
      }}
    >
      {children}
    </div>
  )
}

/** The tooltip's title row — the date, item or reason being read. */
export function TipTitle({ children }: { children: ReactNode }) {
  return (
    <div style={{
      fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 14,
      marginBottom: 4, paddingBottom: 4,
      borderBottom: '1px solid var(--color-divider)',
    }}>{children}</div>
  )
}

/** One label/value line, with an optional colour swatch matching the series. */
export function TipRow({ label, value, color }: {
  label: string
  value: ReactNode
  color?: string
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: 8,
      justifyContent: 'space-between',
    }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        color: 'var(--color-neutral-700)', minWidth: 0,
      }}>
        {color ? (
          <span style={{
            width: 9, height: 9, flex: 'none', display: 'inline-block', background: color,
          }} />
        ) : null}
        <span className="truncate">{label}</span>
      </span>
      <span className="num" style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{value}</span>
    </div>
  )
}

/** The vertical rule marking the hovered band inside the SVG. */
export function HoverGuide({ x, height }: { x: number; height: number }) {
  return (
    <line
      x1={x} y1="0" x2={x} y2={height}
      stroke="var(--color-text)" strokeWidth="1" strokeDasharray="3 3"
      opacity="0.45" vectorEffect="non-scaling-stroke" pointerEvents="none"
    />
  )
}
